/**
 * G16 project ingest and continuous sync (V37). Everything here travels ONLY inside encrypted
 * entities, and every entity has exactly ONE writer role (same model as G9 feedback):
 * - `ingestcfg` role `config` (one per project; the user's devices): sources, consent, exclusions,
 *   LLM review budget, pause.
 * - `ingestcfg` role `status` (one per project × machine; that daemon): what is indexed, cursors,
 *   last sync, budget use, workflow signals.
 * - `ingestcfg` role `decision` (one per item; the user's devices): accept / reject / edit.
 * - `ingest` (one per item; the syncing daemon): an import proposal, a drift observation or a
 *   conflict, with provenance (file + line + commit, or session + excerpt) and confidence.
 *
 * Repo documents and agent sessions are the user's own content, but they are still DATA: text
 * extracted from them is never an instruction to Viko. Accepting an item creates exactly the
 * entity it names (task / goal milestone / workstream / rule / research) and nothing else.
 */
import { z } from 'zod';
import { stableEntityId } from './digests.js';
import { PrivateEntityId } from './encrypted-entities.js';
const Ts = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const Id = PrivateEntityId;
const MachineId = z.string().regex(/^[A-Za-z0-9:_-]{1,128}$/);
export const INGEST_SOURCES = ['docs', 'sessions', 'git'];
export const IngestSource = z.enum(INGEST_SOURCES);
/** What an item is about. */
export const INGEST_ITEM_KINDS = [
    'goal',
    'milestone',
    'task',
    'workstream',
    'decision',
    'blocker',
    'question',
    'followup',
    'retro',
    'research',
    'external-work',
];
export const IngestItemKind = z.enum(INGEST_ITEM_KINDS);
export const INGEST_KIND_LABEL = {
    goal: 'Goal',
    milestone: 'Milestone',
    task: 'Task',
    workstream: 'Workstream',
    decision: 'Decision',
    blocker: 'Blocker',
    question: 'Open question',
    followup: 'Follow-up',
    retro: 'Lesson',
    research: 'Research',
    'external-work': 'Outside Viko',
};
/**
 * - `import`: found in docs/sessions, not yet in Viko (review before anything is written).
 * - `drift`: the project moved outside Viko (commits, branches, worktrees, sessions, doc edits).
 * - `conflict`: the source changed AND the user edited the Viko entity; never auto-resolved.
 * - `insight`: decisions, lessons, open questions and research worth keeping.
 */
export const INGEST_CATEGORIES = ['import', 'drift', 'conflict', 'insight'];
export const IngestCategory = z.enum(INGEST_CATEGORIES);
/** The status the SOURCE gives the work (not Viko's). */
export const IngestSourceStatus = z.enum(['open', 'in-progress', 'blocked', 'done']);
/**
 * - `pending`: waiting for the user. `accepted`/`rejected`: the user decided.
 * - `applied`: the daemon reconciled it (evidence-backed, the entity was untouched by the user).
 * - `superseded`: the source no longer says this (removed from the doc, or replaced).
 */
export const INGEST_STATES = ['pending', 'accepted', 'rejected', 'applied', 'superseded'];
export const IngestState = z.enum(INGEST_STATES);
export const INGEST_EXCERPT_CHARS = 600;
export const IngestProvenance = z.strictObject({
    kind: z.enum(['doc', 'session', 'commit', 'branch', 'worktree']),
    /** Repo-relative path (doc), session file path, or worktree path. */
    path: z.string().max(1000).optional(),
    /** 1-based line in the doc (or record line in the session file). */
    line: z.number().int().positive().max(10_000_000).optional(),
    /** Commit the doc was read at, or the commit itself. */
    commit: z
        .string()
        .regex(/^[0-9a-f]{7,64}$/)
        .optional(),
    branch: z.string().max(250).optional(),
    agent: z.enum(['claude', 'codex']).optional(),
    sessionId: z.string().max(200).optional(),
    /** Scrubbed source text around the match (drill-down without a round trip). */
    excerpt: z.string().max(INGEST_EXCERPT_CHARS).optional(),
    at: Ts.optional(),
});
/** The Viko entity an item matched (dedupe) or targets (reconcile/conflict). */
export const IngestMatch = z.strictObject({
    kind: z.enum(['task', 'goal', 'workstream', 'rule', 'research']),
    id: Id,
    title: z.string().max(300).optional(),
    relation: z.enum(['duplicate', 'update', 'conflict', 'completes']),
    score: z.number().min(0).max(1).optional(),
});
/** Conflict detail: what the source now says vs what the user has in Viko. */
export const IngestConflict = z.strictObject({
    field: z.enum(['title', 'status']),
    source: z.string().max(300),
    viko: z.string().max(300),
});
/** `ingest` entity value (one per item; written only by the syncing daemon). */
export const IngestItemRecord = z.strictObject({
    projectId: Id,
    /** Stable dedupe key (e.g. `doc:.claude/tasks/phase2.md:task:add retries`). */
    key: z.string().min(1).max(300),
    machineId: MachineId,
    source: IngestSource,
    category: IngestCategory,
    kind: IngestItemKind,
    title: z.string().trim().min(1).max(300),
    detail: z.string().max(2000).optional(),
    status: IngestSourceStatus.optional(),
    owner: z.string().max(100).optional(),
    /** Parent (milestone / phase title) the item sits under in its source. */
    parent: z.string().max(300).optional(),
    confidence: z.number().min(0).max(1),
    provenance: z.array(IngestProvenance).min(1).max(12),
    match: IngestMatch.optional(),
    conflicts: z.array(IngestConflict).max(4).optional(),
    state: IngestState,
    /** Entity created on accept, or the one reconciled. */
    entityId: Id.optional(),
    /** How many times this item was seen again (grouped). */
    seen: z.number().int().positive().default(1),
    decidedAt: Ts.optional(),
    createdAt: Ts,
    updatedAt: Ts,
});
/** Globs like `docs/archive/**`, `*.secret.md`; matched against repo-relative paths. */
export const IngestExclusion = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[^\0\n]+$/);
export const IngestConfigRecord = z.strictObject({
    role: z.literal('config'),
    projectId: Id,
    paused: z.boolean().default(false),
    sources: z.strictObject({
        docs: z.strictObject({ enabled: z.boolean().default(true) }),
        /** Consent: sessions are read only when enabled here (or history import consent is on). */
        sessions: z.strictObject({
            enabled: z.boolean().default(false),
            windowDays: z.number().int().min(1).max(90).default(14),
        }),
        git: z.strictObject({ enabled: z.boolean().default(true) }),
    }),
    exclude: z.array(IngestExclusion).max(100).default([]),
    /**
     * Worktree globs whose branches belong to a local agent (never "Outside Viko"). `<repo>` is the
     * repo directory name; relative globs resolve against the repo's parent directory. Absent =
     * `<repo>/../<repo>-wt/*`.
     */
    trustedWorktreeGlobs: z.array(z.string().min(1).max(500)).max(20).optional(),
    /** LLM review of NEW session segments only, cached by segment hash, capped per day. */
    llm: z.strictObject({
        enabled: z.boolean().default(true),
        dailySegments: z.number().int().min(0).max(500).default(5),
    }),
    /** The user asked for a sync now; the owning daemons answer in their status. */
    syncRequestedAt: Ts.optional(),
    updatedAt: Ts,
});
export const defaultIngestConfig = (projectId) => ({
    role: 'config',
    projectId,
    paused: false,
    sources: {
        docs: { enabled: true },
        sessions: { enabled: false, windowDays: 14 },
        git: { enabled: true },
    },
    exclude: [],
    llm: { enabled: true, dailySegments: 5 },
    updatedAt: 0,
});
export const IngestCursorView = z.strictObject({
    agent: z.enum(['claude', 'codex']),
    sessionId: z.string().max(200),
    path: z.string().max(1000),
    /** Bytes consumed (complete lines) vs the file size at the last look. */
    offset: z.number().int().nonnegative(),
    size: z.number().int().nonnegative(),
    lastReadAt: Ts.optional(),
    external: z.boolean(),
    title: z.string().max(200).optional(),
});
const SourceRun = z.strictObject({
    enabled: z.boolean(),
    lastSyncAt: Ts.optional(),
    lastError: z.string().max(500).optional(),
});
export const IngestStatusRecord = z.strictObject({
    role: z.literal('status'),
    projectId: Id,
    machineId: MachineId,
    paused: z.boolean(),
    docs: SourceRun.extend({
        files: z
            .array(z.strictObject({ path: z.string().max(1000), items: z.number().int() }))
            .max(200),
    }),
    sessions: SourceRun.extend({
        indexed: z.number().int().nonnegative(),
        /** Bytes read in the last pass (0 when nothing changed: the cache worked). */
        bytesRead: z.number().int().nonnegative(),
        cacheHits: z.number().int().nonnegative(),
        cursors: z.array(IngestCursorView).max(40),
    }),
    git: SourceRun.extend({
        branch: z.string().max(250).optional(),
        head: z.string().max(64).optional(),
        intervalMs: z.number().int().nonnegative().optional(),
    }),
    llm: z.strictObject({
        day: z.string().max(10),
        used: z.number().int().nonnegative(),
        budget: z.number().int().nonnegative(),
        cached: z.number().int().nonnegative(),
    }),
    /** V09 workflow-efficiency signals from sessions (counts over the indexed window). */
    signals: z.strictObject({
        corrections: z.number().int().nonnegative(),
        repeatedFailures: z.number().int().nonnegative(),
        unansweredAsks: z.number().int().nonnegative(),
        /** agent-sharing: "couldn't attach/send it, look on the Mac" messages. */
        shareFailures: z.number().int().nonnegative().optional(),
    }),
    /** Answer to the config's `syncRequestedAt`. */
    syncedRequestAt: Ts.optional(),
    updatedAt: Ts,
});
export const IngestEdits = z.strictObject({
    title: z.string().trim().min(1).max(300).optional(),
    status: IngestSourceStatus.optional(),
    /** Accept as a different entity kind (e.g. a follow-up as a task). */
    as: z.enum(['task', 'milestone', 'workstream', 'rule', 'research']).optional(),
});
export const IngestDecisionRecord = z.strictObject({
    role: z.literal('decision'),
    projectId: Id,
    itemId: Id,
    action: z.enum(['accept', 'reject']),
    edits: IngestEdits.optional(),
    /** accept: the entity the deciding device created or updated. */
    entityKind: z.enum(['task', 'goal', 'workstream', 'rule', 'research']).optional(),
    entityId: Id.optional(),
    at: Ts,
});
export const IngestCfgEntity = z.discriminatedUnion('role', [
    IngestConfigRecord,
    IngestStatusRecord,
    IngestDecisionRecord,
]);
/**
 * Deterministic ids for what the user's devices write: a retried decision/accept never mints a
 * second entity. (Daemon-written items and status records get their ids from create intents.)
 */
export const ingestConfigId = (projectId) => stableEntityId('ingest-config', PrivateEntityId.parse(projectId));
export const ingestDecisionId = (itemId) => stableEntityId('ingest-decision', PrivateEntityId.parse(itemId));
export const ingestEntityId = (itemId, kind) => stableEntityId(`ingest-${kind}`, PrivateEntityId.parse(itemId));
/** Normalized text for dedupe keys (case, punctuation, markdown and whitespace insensitive). */
export function ingestNorm(s) {
    return s
        .toLowerCase()
        .replace(/[`*_~[\]()#>]/g, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .slice(0, 160);
}
/** The entity kind an accepted item becomes (the user may override with `edits.as`). */
export function ingestTargetKind(item, edits) {
    if (edits?.as)
        return edits.as;
    switch (item.kind) {
        case 'task':
        case 'followup':
        case 'blocker':
        case 'question':
            return 'task';
        case 'milestone':
            return 'milestone';
        case 'goal':
            return 'goal';
        case 'workstream':
            return 'workstream';
        case 'external-work':
            // A branch/worktree made elsewhere can become a lane; a commit or session is acknowledged.
            return item.provenance?.some((p) => p.kind === 'branch' || p.kind === 'worktree')
                ? 'workstream'
                : undefined;
        case 'retro':
            return 'rule';
        case 'research':
            return 'research';
        case 'decision':
            return undefined;
    }
}
const TASK_STATUS = {
    open: 'ready',
    'in-progress': 'ready',
    blocked: 'blocked',
    done: 'done',
};
/** Where an item came from, for the task's provenance string (`ingest:<itemId>`) and notes. */
export function describeProvenance(p) {
    if (p.kind === 'doc')
        return `${p.path ?? 'doc'}${p.line ? `:${p.line}` : ''}${p.commit ? ` @${p.commit.slice(0, 7)}` : ''}`;
    if (p.kind === 'session')
        return `${p.agent ?? 'agent'} session ${p.sessionId ?? ''}`.trim();
    if (p.kind === 'commit')
        return `commit ${p.commit?.slice(0, 7) ?? ''}`.trim();
    if (p.kind === 'branch')
        return `branch ${p.branch ?? ''}`.trim();
    return `worktree ${p.path ?? ''}`.trim();
}
/** Task entity value for an accepted item (id = {@link ingestEntityId}(itemId, 'task')). */
export function ingestTaskRecord(itemId, item, edits, now) {
    PrivateEntityId.parse(itemId);
    const status = TASK_STATUS[edits?.status ?? item.status ?? 'open'];
    const where = item.provenance.map(describeProvenance).join('; ');
    return {
        projectId: item.projectId,
        workstreamId: null,
        title: (edits?.title ?? item.title).slice(0, 300),
        description: [item.detail, `Imported from ${where}.`].filter(Boolean).join('\n\n'),
        status,
        labels: ['imported', `ingest:${item.source}`, ...(item.parent ? [`from:${item.parent}`] : [])]
            .map((l) => l.slice(0, 120))
            .slice(0, 8),
        deps: [],
        acceptance: [],
        provenance: `ingest:${itemId}`,
        proposed: false,
        priority: item.kind === 'blocker' ? 7 : 5,
        agentHint: null,
        requires: [],
        rationale: null,
        progress: null,
        blockedReason: status === 'blocked' ? (item.detail ?? 'Blocked (from the source)') : null,
        runId: null,
        ideaId: null,
        notBefore: null,
        parked: null,
        landedAt: status === 'done' ? now : null,
        createdAt: now,
        updatedAt: now,
    };
}
/** Workstream entity value for an accepted workstream / external-branch item. */
export function ingestWorkstreamRecord(itemId, item, edits, now) {
    PrivateEntityId.parse(itemId);
    const branch = item.provenance.find((p) => p.branch)?.branch ?? null;
    const worktree = item.provenance.find((p) => p.kind === 'worktree')?.path ?? null;
    return {
        projectId: item.projectId,
        name: (edits?.title ?? item.title).slice(0, 200),
        branch,
        worktree,
        machineId: item.machineId,
        brief: item.detail ?? null,
        status: 'idle',
        ideaId: null,
        liveRun: null,
        bulkApproved: false,
        paused: false,
        agent: null,
        counts: { open: 0, proposed: 0, done: 0 },
        createdBy: 'user',
        createdAt: now,
        updatedAt: now,
    };
}
/** Rule entity value for an accepted lesson (a rules suggestion from corrections/retros). */
export function ingestRuleRecord(itemId, item, edits, now) {
    PrivateEntityId.parse(itemId);
    return {
        scope: 'project',
        projectId: item.projectId,
        workstreamId: null,
        text: (edits?.title ?? item.title).slice(0, 2000),
        provenance: `ingest:${itemId}`,
        status: 'active',
        evidence: item.provenance.map(describeProvenance).slice(0, 8),
        lastCitedAt: null,
        citeCount: 0,
        createdAt: now,
        updatedAt: now,
    };
}
/** Only http(s) URLs found in an item become research sources. */
export function ingestResearchSources(item) {
    const seen = new Set();
    const out = [];
    const text = [item.detail ?? '', ...item.provenance.map((p) => p.excerpt ?? '')].join('\n');
    for (const m of text.matchAll(/https?:\/\/[^\s)<>"'\]]+/g)) {
        const url = m[0].replace(/[.,;:]+$/, '');
        if (seen.has(url) || url.length > 2000)
            continue;
        seen.add(url);
        out.push({ url, title: url.slice(0, 200) });
        if (out.length >= 10)
            break;
    }
    return out;
}
/** What the optional LLM review of a session segment may return (validated; no tools). */
export const IngestReviewItem = z.strictObject({
    kind: z.enum(['followup', 'decision', 'question', 'retro', 'research', 'task']),
    title: z.string().trim().min(3).max(300),
    detail: z.string().max(1000).optional(),
});
export const IngestReviewResult = z.strictObject({ items: z.array(IngestReviewItem).max(20) });
//# sourceMappingURL=ingest.js.map